import { pageFaqAdditions } from "@/data/pageFaqs";
import { createFileRoute } from "@tanstack/react-router";
import { cooLoadedCostSentence } from "@/data/benchmarks";
import {
  absoluteUrl,
  breadcrumbSchema,
  canonicalLinks,
  faqPageSchema,
  jsonLd,
  ogImage,
  ogUrl,
  serviceSchema,
  webPageSchema,
} from "@/lib/seo";
import { LocationPage } from "@/components/site/LocationPage";

const PATH = "/fractional-coo-central-coast";
const SERVICE_ID = `${absoluteUrl(PATH)}#service`;
const TITLE = "Fractional COO Central Coast | Ajility";
const DESCRIPTION =
  "Fractional COO support for founder-led Central Coast businesses doing $3M to $30M. Trades, manufacturing, health and professional services.";

const faqs = [
  {
    q: "Our business is too small for a COO. Is this still relevant?",
    a: "It is built for that situation. Most Central Coast businesses between $3M and $30M cannot justify a full-time COO salary and do not need one. Most composed retainers for businesses under $15M start from $3,900 per month plus GST, and many engagements begin with the Operating Diagnostic from $3,600 plus GST to test whether the problem is what the founder thinks it is.",
  },
  {
    q: "How often would you be here?",
    a: "The Central Coast is inside the normal working radius, so a retainer typically runs a full on-site day each week or each fortnight from Gosford through to Wyong and Tuggerah. The weekly leadership session is held in person wherever practical.",
  },
  {
    q: "Half our revenue comes from Sydney clients. Does that change anything?",
    a: "It changes the operating model, not the engagement. Businesses that deliver into Sydney from a Central Coast base carry travel time, crew scheduling and quoting questions that a purely local business does not. Recovering that travel cost properly in the quote is a common early fix.",
  },
  {
    q: "Can you help us decide whether to hire a full-time operations manager?",
    a: "Yes, and the honest answer is often that you should. Where the work is genuinely a full-time role, the sound sequence is to define the role and the scorecard first, run the interim period on a fractional basis, then recruit into a defined seat rather than hiring someone into ambiguity and hoping it resolves.",
  },
  ...pageFaqAdditions["/fractional-coo-central-coast"],
];

export const Route = createFileRoute("/fractional-coo-central-coast")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Fractional COO and GM Central Coast | Ajility" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Fractional COO and GM Central Coast | Ajility" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Fractional COO on the Central Coast" },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage("fractional-coo-central-coast", "Fractional COO services on the Central Coast from Ajility"),
      ogUrl(PATH),
    ],
    links: canonicalLinks(PATH),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Fractional COO Central Coast", path: PATH }])),
      jsonLd(
        webPageSchema({ image: "fractional-coo-central-coast",
          path: PATH,
          name: TITLE,
          description: DESCRIPTION,
          mainEntityId: SERVICE_ID,
        }),
      ),
      jsonLd({
        ...serviceSchema({
          name: "Fractional COO Central Coast",
          description: DESCRIPTION,
          path: PATH,
          slug: "fractional-coo-central-coast",
          serviceType: "Fractional COO",
          offerId: ["operational-diagnostic", "full-diagnostic-roadmap", "fortnightly-rhythm-base"],
        }),
        areaServed: [
          { "@type": "AdministrativeArea", name: "Central Coast, New South Wales" },
          { "@type": "City", name: "Gosford" },
          { "@type": "City", name: "Erina" },
          { "@type": "City", name: "Tuggerah" },
          { "@type": "City", name: "Wyong" },
        ],
      }),
      jsonLd(faqPageSchema(PATH, faqs, SERVICE_ID)),
    ],
  }),
  component: CentralCoastPage,
});

function CentralCoastPage() {
  return (
    <LocationPage
      city="Central Coast"
      region="NSW"
      heading="Fractional COO on the Central Coast"
      lead="Operating leadership for Gosford, Erina, Tuggerah and Wyong businesses that have outgrown the founder's capacity and cannot justify a full-time executive salary. Most Australian businesses call this seat the general manager; a fractional GM and a fractional COO are the same role."
      intro={[
        "The Central Coast has a business profile that sits between a capital city and a regional centre. Around 350,000 residents, a substantial trade and construction base, light manufacturing through the Somersby and Tuggerah industrial estates, a large health and aged care sector, and a growing group of professional services firms whose clients are largely in Sydney. Very few of these businesses are venture funded or private equity owned. Almost all of them are founder-led.",
        "That profile creates a specific constraint. A business doing $8M with thirty staff has genuinely outgrown one person's capacity, but cannot sensibly carry a full-time executive. " + cooLoadedCostSentence() + " The local market for experienced operators is also thin. The founder's realistic options are to promote someone who has never held the role, to recruit from Sydney and hope the commute survives the honeymoon, or to keep absorbing the work personally.",
        "Fractional operating leadership resolves that directly. Senior capability, on site weekly, priced against a deliverable rather than a salary, with the explicit intention of building the internal capability that eventually replaces it.",
      ]}
      industries={[
        {
          name: "Trades and construction",
          note: "Builders, civil contractors and specialist trades working across the Coast and into Sydney's north. Job costing, quoting rates that recover travel and setup, and disciplined variation claiming are the three levers that move net margin here more than any sales activity.",
        },
        {
          name: "Light manufacturing and fabrication",
          note: "Somersby, West Gosford and Tuggerah businesses producing for regional and Sydney customers. Scheduling, changeover discipline and workshop rate recovery decide the result, and most operate without a weekly production number.",
        },
        {
          name: "Health, aged care and allied services",
          note: "One of the largest employer groups on the Coast. Multi-site providers under funded pricing where roster efficiency, occupancy and clean claiming are the only real levers, since the revenue per unit is set externally.",
        },
        {
          name: "Professional and technical services",
          note: "Engineering, design, accounting and IT firms delivering largely into Sydney. Utilisation, scope discipline and a genuine handle on the cost of unbilled work are usually where the recoverable margin sits.",
        },
        {
          name: "Tourism, hospitality and food",
          note: "Seasonal operators from Terrigal through to the lakes. Labour cost as a percentage of a highly variable revenue line is the core discipline, and it requires forward rostering against forecast rather than reaction after the fact.",
        },
      ]}
      sections={[
        {
          heading: "How Central Coast engagements run",
          paragraphs: [
            "The Coast is inside the normal working radius, so this is an on-site engagement rather than a remote one. A retainer usually runs a full day each week, or each fortnight for smaller engagements, spent inside the business from Gosford and Erina through Tuggerah and Wyong. The weekly leadership session is held in person wherever practical.",
            "The day is deliberately structured. Leadership session, one-to-one work with the people carrying quarterly priorities, a walk through the workshop, site or clinic, and the commercial review when it falls. Where the business delivers into Sydney, part of the engagement is spent with the crews or teams working there, since the difference between the schedule and the reality is visible on the job and not in the office.",
            "Between visits the work continues remotely, and founders retain access for the decisions that cannot wait a week. Fixed-scope work, such as the Operating Diagnostic and 90-Day Roadmap from $3,600 plus GST, runs to milestone dates instead of a standing day. Pricing is national and there is no regional loading or discount.",
          ],
        },
        {
          heading: "What tends to be wrong in a Central Coast business",
          paragraphs: [
            "The most common pattern is a founder who is still the operations manager, the sales lead and the final approver on everything. The business runs well up to the ceiling of that person's week and then plateaus. Nobody notices the plateau because everyone is busy, and busyness is easily mistaken for capacity.",
            "The second is that leadership roles exist as titles without scorecards. Someone is called the operations manager and has no defined numbers, no authority limits and no forum in which their performance is reviewed. That is not a people problem. It is a structural one, and it is fixable inside a quarter.",
            "The third is a quoting model that has never been rebuilt for Sydney delivery. Businesses that grew locally and then followed clients south often price the work as though the crew were still fifteen minutes from the yard. Travel time, vehicle cost and lost productive hours quietly consume the margin on exactly the work the founder considers the growth engine.",
          ],
        },
      ]}
      example={{
        label: "Anonymised Central Coast example",
        title: "A trade business whose Sydney growth was its least profitable work",
        body: [
          "A specialist trade business based near Tuggerah, founder-led and about $9M in revenue, had doubled over three years by taking on Sydney projects. Revenue growth was obvious. Profit had not moved. The founder was working six days and had begun quoting at night because there was no other time.",
          "The Operating Diagnostic took two weeks and produced one dominant finding. Jobs were quoted on a single hourly rate regardless of location, so the Sydney work, which carried two hours of unpaid travel per crew per day plus higher parking and access costs, was being delivered at close to break even while the local work carried the business.",
          "Two changes followed. A location-based rate that recovered travel and access, and a minimum job size for Sydney work so a crew was never sent south for half a day. Alongside that, the most experienced supervisor was given a defined role, three numbers and authority to approve variations under a set threshold. The founder stopped quoting at night within a month.",
        ],
      }}
      relatedLinks={[
        {
          to: "/services/team-structure",
          label: "People, Culture and Accountability",
          note: "Roles, decision rights and accountability for Central Coast businesses where the founder is still the escalation point.",
        },
        {
          to: "/services/systems-improvement",
          label: "Systems and Operational Improvement",
          note: "Practical process and systems work where growth has been absorbed by manual effort.",
        },
        {
          to: "/scan",
          label: "Operating Scan",
          note: "A free self-assessment that takes about six minutes and returns where your constraint most likely sits.",
        },
      ]}
      cta={{
        heading: "A local operator on the Central Coast",
        body: "Central Coast engagements run with regular days on site rather than a monthly visit. A thirty-minute fit call is the starting point and it is not a pitch.",
        linkLabel: "Book a fit call",
      }}
      faqs={faqs}
    />
  );
}
